import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import TestimonialSlider from '../components/TestimonialSlider';
import { ShoppingBag, CheckCircle } from 'lucide-react';

const AmazonBrandRegistry = () => {
  const steps = [
    {
      title: 'Trademark Registration',
      description: 'First, we secure your trademark registration with the USPTO, as an active or pending trademark is required for Amazon Brand Registry enrollment.',
    },
    {
      title: 'Brand Registry Application',
      description: 'Once your trademark is approved or pending, we assist with the Amazon Brand Registry application, gathering all necessary brand information and documentation.',
    },
    {
      title: 'Enrollment and Setup',
      description: 'We guide you through the enrollment process, help set up brand protection tools, and ensure your brand is fully protected on Amazon\'s marketplace.',
    },
  ];

  const packages = [
    {
      name: 'STANDARD',
      price: '$99',
      priceNote: '+ USPTO filing fee',
      features: [
        'Amazon Brand Case Review',
        'Amazon Brand Case Filing',
        '100% Satisfaction Guarantee',
      ],
    },
    {
      name: 'DIAMOND',
      price: '$149',
      priceNote: '+ USPTO filing fee',
      features: [
        'Amazon Brand Case Review',
        'Amazon Brand Case Preparation',
        'Amazon Brand Case Filing',
        '100% Satisfaction Guarantee',
        'Refusal Risk Meter',
      ],
      popular: true,
    },
    {
      name: 'PREMIUM',
      price: '$199',
      priceNote: '+ USPTO filing fee',
      features: [
        'Amazon Brand Case Review',
        'Amazon Brand Case Preparation',
        'Amazon Brand Case Filing',
        '100% Satisfaction Guarantee',
        'Refusal Risk Meter',
        'Amazon Brand Case Monitoring',
      ],
    },
  ];

  const usps = [
    {
      title: 'Amazon Specialists',
      description: 'Our team includes experts who specialize in Amazon Brand Registry and marketplace protection. We understand Amazon\'s requirements and help you navigate the enrollment process smoothly.',
    },
    {
      title: 'Complete Solution',
      description: 'We handle both trademark registration and Brand Registry enrollment, providing a seamless end-to-end service. No need to coordinate with multiple providers or worry about missing steps.',
    },
    {
      title: 'Brand Protection',
      description: 'Beyond enrollment, we help you leverage Brand Registry\'s powerful tools including proactive brand protection, enhanced content options, and detailed analytics to grow your Amazon business.',
    },
    {
      title: 'Proven Success',
      description: 'We have successfully enrolled hundreds of brands in Amazon Brand Registry, helping them protect their products, enhance their listings, and increase sales on the platform.',
    },
  ];

  const testimonials = [
    {
      name: 'Steven Clark',
      company: 'Home Goods Seller',
      text: 'Fine Trademark handled everything from trademark to Brand Registry enrollment. My products are now protected, and I can use enhanced content. Excellent service!',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=200&h=200&fit=crop',
    },
    {
      name: 'Michelle Davis',
      company: 'Fashion Brand',
      text: 'The complete package was worth it. They got my trademark approved and set up Brand Registry perfectly. My listings look professional now.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&h=200&fit=crop',
    },
    {
      name: 'Brian Wilson',
      company: 'Electronics Seller',
      text: 'Fast and professional. They understood Amazon\'s requirements and made the entire process straightforward. Highly recommend!',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1463453091185-61582044d556?w=200&h=200&fit=crop',
    },
    {
      name: 'Patricia Moore',
      company: 'Beauty Products',
      text: 'The premium package transformed my Amazon presence. A+ content, brand protection, and great support. Best investment for my business.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop',
    },
    {
      name: 'James Taylor',
      company: 'Sporting Goods',
      text: 'They made Brand Registry enrollment easy. Great communication and expertise in both trademark law and Amazon requirements.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=200&h=200&fit=crop',
    },
  ];

  const faqs = [
    {
      question: 'What is Amazon Brand Registry?',
      answer: 'Amazon Brand Registry is a program that helps brand owners protect their intellectual property and create an accurate brand presence on Amazon. It provides tools for brand protection, enhanced content, and detailed analytics.',
    },
    {
      question: 'Do I need a trademark to enroll in Brand Registry?',
      answer: 'Yes, you need an active registered trademark or a pending trademark application with the USPTO (or equivalent trademark office in other countries). We can help you obtain your trademark as part of our service.',
    },
    {
      question: 'What benefits does Brand Registry provide?',
      answer: 'Brand Registry offers proactive brand protection tools, the ability to report violations, access to A+ Content, Stores, Sponsored Brands advertising, detailed analytics, and more control over your product listings.',
    },
    {
      question: 'How long does the enrollment process take?',
      answer: 'Once you have an active or pending trademark, Brand Registry enrollment typically takes 1-2 weeks. The complete process including trademark registration takes 8-12 months, but you can enroll with a pending trademark.',
    },
    {
      question: 'Can you help if I already have a trademark?',
      answer: 'Absolutely! If you already have a registered trademark, we can assist with just the Brand Registry enrollment and setup, helping you leverage all available brand protection and marketing tools.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1E1E1E] via-[#2a2a2a] to-[#1E1E1E] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="page-title text-white mb-6 animate-in fade-in slide-in-from-top duration-700">
              Amazon Brand Registry Services
            </h1>
            <p className="body-large text-gray-300 mb-8 animate-in fade-in slide-in-from-bottom duration-700">
              Protect your brand on Amazon with comprehensive Brand Registry services. Stop counterfeiters, enhance your listings, and grow your sales.
            </p>
            <Link to="/contact-us">
              <Button size="lg" className="bg-[#E5B80B] hover:bg-[#c99f09] text-[#1E1E1E] font-semibold px-12 text-lg h-14 transition-all duration-300 hover:scale-105">
                Enroll in Brand Registry
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Service */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="section-heading">About Amazon Brand Registry</h2>
              <p className="body-regular">
                Amazon Brand Registry is essential for brand owners selling on Amazon's marketplace. This program provides powerful tools to protect your intellectual property, prevent counterfeit products, and maintain control over your product listings. With Brand Registry, you gain access to enhanced marketing features and proactive brand protection.
              </p>
              <p className="body-regular">
                Our comprehensive service handles the entire process from trademark registration to Brand Registry enrollment and setup. We ensure you meet all requirements, complete the application correctly, and maximize the benefits of brand protection tools available through the program.
              </p>
            </div>
            <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=800"
                alt="Amazon Brand Registry"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-6">Our Enrollment Process</h2>
            <p className="body-large max-w-3xl mx-auto">
              We provide a complete solution for Amazon Brand Registry enrollment, starting with trademark protection.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 rounded-full bg-[#E5B80B] text-[#1E1E1E] flex items-center justify-center text-3xl font-bold mb-6 shadow-lg">
                    {index + 1}
                  </div>
                  <h3 className="sub-heading mb-4">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-1/2 w-full h-0.5 bg-[#E5B80B]/30"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-6">Brand Registry Packages</h2>
            <p className="body-large max-w-3xl mx-auto">
              Choose the package that best fits your Amazon business needs. All packages include expert guidance and support.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <Card key={index} className={`relative ${pkg.popular ? 'border-4 border-[#E5B80B] shadow-2xl scale-105' : 'border-2'}`}>
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-[#E5B80B] text-[#1E1E1E] text-sm px-4 py-1">Most Popular</Badge>
                  </div>
                )}
                <CardContent className="pricing-card p-6">
                  <div className="pricing-header">
                    <h3 className="card-title text-white mb-2">{pkg.name}</h3>
                    <div className="border-t border-white/30 w-12 mx-auto my-2"></div>
                    <span className="text-4xl font-bold">{pkg.price}</span>
                  </div>
                  <div className="pricing-features mt-6">
                    <ul className="space-y-3">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-[#E5B80B] mr-2 mt-0.5 flex-shrink-0" />
                          <span className="body-small text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-xs text-gray-500 mb-4 text-center mt-4">{pkg.priceNote}</p>
                  <Link to="/contact-us">
                    <Button className={`w-full h-12 font-semibold transition-all duration-300 mt-auto ${
                      pkg.popular
                        ? 'bg-[#E5B80B] hover:bg-[#c99f09] text-[#1E1E1E]'
                        : 'bg-[#1E1E1E] hover:bg-[#2a2a2a] text-white'
                    }`}>
                      Get Started
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-6">Why Choose Our Registry Services</h2>
            <div className="max-w-3xl mx-auto space-y-6">
              <p className="body-regular">
                Selling on Amazon without Brand Registry leaves your products vulnerable to counterfeiters and unauthorized sellers. Our team specializes in both trademark law and Amazon's Brand Registry requirements, ensuring you get complete protection for your brand.
              </p>
              <p className="body-regular">
                We don't just help you enroll—we maximize the value of Brand Registry by setting up all available tools, training you on brand protection features, and providing ongoing support to help you grow your Amazon business with confidence.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {usps.map((usp, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <h3 className="sub-heading mb-4">{usp.title}</h3>
                  <p className="body-regular">{usp.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#E5B80B] to-[#c99f09]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="section-heading mb-6">Protect Your Amazon Brand Today</h2>
          <p className="body-large text-[#1E1E1E]/90 mb-8">
            Stop counterfeiters and unauthorized sellers. Enroll in Amazon Brand Registry and take control of your brand.
          </p>
          <Link to="/contact-us">
            <Button size="lg" className="bg-[#1E1E1E] hover:bg-[#2a2a2a] text-white font-semibold px-12 text-lg h-14 transition-all duration-300 hover:scale-105">
              Start Your Enrollment
            </Button>
          </Link>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="section-heading mb-4">Client Success Stories</h2>
          </div>
          <TestimonialSlider testimonials={testimonials} />
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white border border-gray-200 rounded-lg px-6">
                <AccordionTrigger className="text-left text-lg font-semibold text-[#1E1E1E] hover:text-[#E5B80B]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default AmazonBrandRegistry;
